import React, { FC, useEffect, useState } from 'react';
import {
  ArrowRightIcon,
  Box,
  Button,
  GridContainerWithTitle,
  HorizontalDivider,
  Typography,
  VisibilityBox,
} from '@ttx/design-system';
import { useBreakpoint } from '@ttx/design-system/src/use-breakpoint';
import { usePrivy } from '@privy-io/react-auth';
import { Item } from '../squareup/purchase-item';
import { useToastContext } from '../../../../contexts/toast-context';
import { SquareupCardCustomForm } from './squareupCardCustomForm';
import { PaymentErrorModel } from './payment-error-model';
import { PaymentSuccessModel } from './payment-success-model';
import { TransactionStatusEnum } from '../stripe/credit-card-form';

interface ContractInfo {
  contractId: string;
  walletAddress: null | `0x${string}`;
  _listingId: string;
  _currency?: string;
  _price: string;
  _quantityToBuy: number;
  _buyer: null | `0x${string}`;
  _proof: string[];
  _signatureTimestamp: number;
  signature: string;
  price: string;
  currency?: string | null;
  tokenId?: string | number;
}

interface PolymeshNftInfo {
  instructionId: string | undefined;
  currency: string | undefined;
  tokenId: string | undefined;
  priceInDollars: string | undefined;
  slug: string | undefined;
  buyerDid: string | undefined;
}

interface SquareupCheckoutModalProps {
  onModalClose: () => void;
  imageSrc?: string;
  isMobile?: boolean;
  trackName?: string;
  artist?: string;
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  total: string | null;
  transactionFee: string | null;
  contractInfo?: ContractInfo;
  polymeshNftInfo?: PolymeshNftInfo;
  isWidgetVisible: boolean;
  isLoading: boolean;
  network: string;
  isJoshSavage?: boolean;
  onTxnSuccess?: () => void;
}

export const SquareupCheckoutModal: FC<SquareupCheckoutModalProps> = ({
  onModalClose,
  imageSrc,
  trackName,
  artist,
  quantity,
  onIncrement,
  onDecrement,
  transactionFee,
  total,
  contractInfo,
  polymeshNftInfo,
  isWidgetVisible,
  isLoading,
  network,
  onTxnSuccess,
  ...props
}) => {
  const { user: privyUser } = usePrivy();
  const { currentBreakpoint } = useBreakpoint();
  const mobileView = currentBreakpoint === '768px';
  const toast = useToastContext();

  // Payment status and tracking
  const [transactionStatus, setTransactionStatus] = useState<'started' | 'loading' | 'success' | 'error'>('started');
  const [trackingId, setTrackingId] = useState<string>('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const onSquareupCheckout = async () => {
    if (!total || Number(total) <= 0) {
      toast.addErrorToast('Invalid purchase amount');
      return;
    }
    setTransactionStatus('loading');
    setShowPaymentForm(true);
    setTimeout(() => setTransactionStatus('started'), 100);
  };

  return (
    <Box w="100%" maxWidth={[366, 568]} paddingX={['two', 'zero']}>
      <GridContainerWithTitle
        title={
          (network !== 'polymesh' && transactionStatus === 'success') ||
          transactionStatus === 'error'
            ? 'Transaction Complete'
            : 'Checkout'
        }
        onModalClose={onModalClose}
        backgroundColor="surfaceBlack"
      >
        <Item
          imageSrc={imageSrc}
          isMobile={mobileView}
          label={trackName as string}
          artist={artist as string}
          tokenId={polymeshNftInfo?.tokenId as string}
          quantity={quantity}
          hideQtyButton={
            transactionStatus === 'success' ||
            transactionStatus === 'error'
              ? true
              : isWidgetVisible
          }
          onIncrement={() => {
            if (isWidgetVisible) return;
            onIncrement();
          }}
          onDecrement={() => {
            if (isWidgetVisible) return;
            onDecrement();
          }}
        />

        {(transactionStatus === 'started' || transactionStatus === 'loading') ? (
          <>
            {!props.isJoshSavage && (
              <>
                <Box paddingLeft="eight" paddingRight="eight">
                  <HorizontalDivider color="grey12" />
                </Box>
                <Box
                  paddingLeft="eight"
                  paddingRight="eight"
                  paddingTop="two"
                  paddingBottom="four"
                >
                  <Box flexDirection="row">
                    <Box flex="1">
                      <Typography fontSize="s" color="textLow">
                        Service fee
                      </Typography>
                      <Typography fontSize="h3" color="textDefault">
                        {String(Number(transactionFee)?.toFixed(2))}
                      </Typography>
                    </Box>
                    <Box flex="1">
                      <Typography fontSize="s" color="textLow">
                        Total (includes Gas)
                      </Typography>
                      <Typography fontSize="h3" color="textDefault">
                        {`${String(Number(total)?.toFixed(2))}`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
            {!showPaymentForm ? (
              <Box
                flexDirection="row"
                borderWidth="one"
                borderTopWidth="zero"
                borderStyle="solid"
                borderColor="outlines"
                flex={1}
                bg="surfaceDark"
              >
                <Box flex={1}>
                  <VisibilityBox show={[true, true]}>
                    <Button
                      type="primary"
                      size="M"
                      onPress={onSquareupCheckout}
                      buttonStyle={{margin: '8px 24px 16px 24px'}}
                      leftAccessory={<ArrowRightIcon color="textInvert" />}
                      loading={transactionStatus === 'loading'}
                      disabled={Number.isNaN(Number(total))}
                    >
                      Confirm purchase
                    </Button>
                  </VisibilityBox>
                </Box>
              </Box>
            ) : (
              <Box padding="eight">
                {(() => {
                  let metadata: Record<string, any> = {};
                  let description = '';
                  let receipt_email = '';
                  if (network === 'ethereum' || network === 'polygon') {
                    metadata = {
                      buyerWalletAddress: contractInfo?.walletAddress,
                      buyoutPrice: contractInfo?._price,
                      currency: contractInfo?._currency,
                      listingId: contractInfo?._listingId,
                      currentStamp: contractInfo?._signatureTimestamp,
                      proof: contractInfo?._proof,
                      purchaseQuantity: contractInfo?._quantityToBuy,
                      sigHash: contractInfo?.signature,
                      network,
                    };
                    description = `Purchase of ${trackName || ''} by ${artist || ''}`;
                    receipt_email = privyUser?.email?.address
                      ? privyUser.email.address
                      : (typeof privyUser?.email === 'string' ? privyUser.email : (privyUser?.email ? String(privyUser.email) : ''));
                  } else if (network === 'polymesh') {
                    metadata = {
                      instructionId: polymeshNftInfo?.instructionId,
                      currency: polymeshNftInfo?.currency,
                      tokenId: polymeshNftInfo?.tokenId,
                      price: polymeshNftInfo?.priceInDollars,
                      slug: polymeshNftInfo?.slug,
                      buyerDid: polymeshNftInfo?.buyerDid,
                      network,
                    };
                    description = `Purchase of ${trackName || ''} by ${artist || ''}`;
                    receipt_email = privyUser?.email?.address
                      ? privyUser.email.address
                      : (typeof privyUser?.email === 'string' ? privyUser.email : (privyUser?.email ? String(privyUser.email) : ''));
                  }
                  return (
                    <SquareupCardCustomForm
                      transactionStatus={transactionStatus}
                      setTransactionStatus={setTransactionStatus}
                      amount={total ? Math.round(Number(total) * 100) : 0}
                      setTrackingId={setTrackingId}
                      trackingId={trackingId}
                      description={description}
                      receipt_email={receipt_email}
                      metadata={metadata}
                      onTxnSuccess={onTxnSuccess}
                    />
                  );
                })()}
              </Box>
            )}
          </>
        ) : (
          network !== 'polymesh' && (
            <>
              {transactionStatus === TransactionStatusEnum.SUCCESS && (
                <Box padding="eight">
                  <PaymentSuccessModel isMobile={false} />
                </Box>
              )}
              {transactionStatus === TransactionStatusEnum.ERROR && (
                <Box padding="eight">
                  <PaymentErrorModel isMobile={false} />
                </Box>
              )}
            </>
          )
        )}
        {/* {network === 'polymesh' && transactionStatus === 'error' && (
          <Box padding="eight">
            <Typography color="brandPrimary" textStyle="h2">
              Uh-oh!
            </Typography>
            <Typography color="textDefault" textStyle="s">
              Something went wrong with your purchase. Please try again.
            </Typography>
          </Box>
        )} */}
      </GridContainerWithTitle>
    </Box>
  );
}; 